import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiPencil, HiTrash } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import './MeasurementUnitIndex.scss';

interface MeasurementUnit {
  id: number;
  name: string;
  symbol: string;
  type: number;
  conversionFactorToBase: number;
  isDefault: boolean;
}

export const MeasurementUnitIndex: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['measurementUnits', 'common']);
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [measurementUnits, setMeasurementUnits] = useState<MeasurementUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchMeasurementUnits = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/measurementunit?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('measurementUnits:messages.fetchError'));
        return;
      }

      setMeasurementUnits(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
      console.table(response.data?.items);
    } catch (error: any) {
      showErrorToast(t('measurementUnits:messages.fetchError'));
      console.error('Error fetching measurement units:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeasurementUnits(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);

  const deleteMeasurementUnit = async (id: number) => {
    if (!window.confirm(t('measurementUnits:messages.deleteConfirm'))) return;

    try {
      const response = await adminApi.call(`/measurementunit/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('measurementUnits:messages.deleteError'));
        return;
      }

      showSuccessToast(t('measurementUnits:messages.deleteSuccess'));
      fetchMeasurementUnits(currentPage, pageSize);
    } catch (error: any) {
      showErrorToast(t('measurementUnits:messages.deleteError') + ": " + error.message);
    }
  };

  const handleCreateMeasurementUnit = () => {
    navigate('/measurement-units/create');
  };

  const handleEditMeasurementUnit = (id: number) => {
    navigate(`/measurement-units/edit/${id}`);
  };

  const getTypeLabel = (type: number) => {
    return type === 0 ? t('measurementUnits:types.length') : t('measurementUnits:types.weight');
  };

  const columns: Column[] = [
    {
      key: 'name',
      title: t('measurementUnits:table.name'),
      sortable: true,
      filterable: true,
      filterKey: 'name',
    },
    {
      key: 'symbol',
      title: t('measurementUnits:table.symbol'),
      sortable: true,
      filterable: true,
      filterKey: 'symbol',
    },
    {
      key: 'type',
      title: t('measurementUnits:table.type'),
      sortable: true,
      render: (_, unit) => getTypeLabel(unit.type),
    },
    {
      key: 'conversionFactorToBase',
      title: t('measurementUnits:table.conversionFactor'),
      sortable: true,
      render: (_, unit) => unit.conversionFactorToBase.toFixed(6),
    },
    {
      key: 'isDefault',
      title: t('measurementUnits:table.isDefault'),
      sortable: true,
      render: (_, unit) => (
        <div className="">
          <span className={`badge ${unit.isDefault ? 'bg-success' : 'bg-danger'}`}>
            <i className={`fa ${unit.isDefault ? 'fa-check' : 'fa-times'} `}></i>
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      title: t('measurementUnits:table.actions'),
      sortable: false,
      width: '150px',
      align: 'center',
      render: (_, unit) => (
        <div className="action-buttons">
          {hasPermission("Update Measurement Unit") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => handleEditMeasurementUnit(unit.id)}
              tooltip={t('common:edit')}
            />
          )}
          {hasPermission("Delete Measurement Unit") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteMeasurementUnit(unit.id)}
              tooltip={t('common:delete')}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('measurementUnits:breadcrumbs.measurementUnits') }
  ];

  return (
    <PageLayout
      title={t('measurementUnits:title')}
      subtitle={t('measurementUnits:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={measurementUnits}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={false}
        refreshable={true}
        addButton={hasPermission("Create Measurement Unit") ? {
          label: t('measurementUnits:addNew'),
          onClick: handleCreateMeasurementUnit
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchMeasurementUnits(currentPage, pageSize)}
        emptyMessage={t('measurementUnits:messages.noMeasurementUnits')}
      />
    </PageLayout>
  );
};
